import React from "react";
import { Link } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import type { ProductReference } from "../../types/types.PIM";
import { ProductReferenceMockTooltip } from "../ProductReferenceMockTooltip/ProductReferenceMockTooltip";
import { getProductReferenceHref } from "../ProductReference/ProductReferenceAttributeDisplay";
import { useTranslation } from "react-i18next";

interface MultiProductReferenceTooltipProps {
  references: ProductReference[];
  isPortfolio: boolean;
  storePath: string;
  adminPath: string;
  isAccessible: (ref: ProductReference) => boolean;
  getRestrictedTooltip: (ref: ProductReference) => string;
  onMoreClick?: () => void;
}

export function MultiProductReferenceTooltip({
  references,
  isPortfolio,
  storePath,
  adminPath,
  isAccessible,
  getRestrictedTooltip,
  onMoreClick,
}: MultiProductReferenceTooltipProps) {
  const { t } = useTranslation();
  const remainingCount = references.length - 1;
  const firstRef = references[0];

  const buildLink = (reference: ProductReference) => (
    <Link
      to={getProductReferenceHref(reference, {
        isPortfolio,
        storePath,
        adminPath,
      })}
    >
      {reference.name}
    </Link>
  );

  const firstRefContent =
    firstRef && isAccessible(firstRef) ? (
      <ProductReferenceMockTooltip
        productRef={firstRef}
        isPortfolio={isPortfolio}
        storePath={storePath}
        adminPath={adminPath}
      >
        {buildLink(firstRef)}
      </ProductReferenceMockTooltip>
    ) : firstRef ? (
      <>
        <span data-tip={getRestrictedTooltip(firstRef)} data-for={firstRef.id}>
          {firstRef.name}
        </span>
        <ReactTooltip delayHide={200} id={firstRef.id} effect="solid" />
      </>
    ) : (
      <span />
    );

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        maxWidth: "100%",
      }}
    >
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {firstRefContent}
      </span>
      {remainingCount > 0 && (
        <span
          onClick={onMoreClick}
          style={{
            whiteSpace: "nowrap",
            flexShrink: 0,
            cursor: onMoreClick ? "pointer" : "default",
          }}
        >
          {`+${remainingCount} ${t("more")}`}
        </span>
      )}
    </span>
  );
}
